import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  BadgeCheck, MapPin, Clock, Mountain, Route as RouteIcon, Bike, CalendarDays,
  Coffee, Utensils, Fuel, Bed, Wrench, Eye, Star, ArrowRight, ExternalLink,
  ChevronRight, Play, AlertTriangle, ImageIcon
} from "lucide-react";
import { fetchRoute, fetchRoutes, buildDirectionsEmbed } from "@/lib/api";
import RouteCard from "@/components/rotta/RouteCard";
import RouteReviews from "@/components/rotta/RouteReviews";
import { Reveal } from "@/lib/motion";
import { splitParagraphs } from "@/lib/text";
import { toUF } from "@/lib/brStates";

const ICONS = { cafe: Coffee, restaurant: Utensils, fuel: Fuel, hotel: Bed, repair: Wrench, viewpoint: Eye };

function youtubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:.*v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : null;
}

export default function RotaDetail() {
  const { slug } = useParams();
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [allRoutes, setAllRoutes] = useState([]);

  useEffect(() => {
    setLoading(true);
    fetchRoute(slug).then((r) => { setRoute(r); setLoading(false); }).catch(() => setLoading(false));
    fetchRoutes().then(setAllRoutes).catch(() => {});
  }, [slug]);

  const recommended = useMemo(() => {
    if (!route) return [];
    const explicit = route.related || [];
    if (explicit.length >= 3) return explicit.slice(0, 3);
    // Fallback: prefer same state, then verified, then alphabetical.
    const currUf = toUF(route.state);
    const seen = new Set([route.slug, ...explicit.map((x) => x.slug)]);
    const pool = allRoutes.filter((r) => !seen.has(r.slug));
    const scored = pool.map((r) => {
      let score = 0;
      const rUf = toUF(r.state);
      if (currUf && rUf === currUf) score += 30;
      if (r.verified) score += 5;
      return { r, score };
    });
    scored.sort((a, b) => b.score - a.score || (a.r.name || "").localeCompare(b.r.name || "", "pt-BR"));
    const need = 3 - explicit.length;
    return [...explicit, ...scored.slice(0, need).map((x) => x.r)];
  }, [route, allRoutes]);

  if (loading) return <div className="pt-40 text-center text-[#B8B8B8]">Carregando rota…</div>;
  if (!route) return <div className="pt-40 text-center text-[#B8B8B8]">Rota não encontrada.</div>;

  const groupPOI = (route.points_of_interest || []).reduce((acc, p) => {
    (acc[p.type] = acc[p.type] || []).push(p);
    return acc;
  }, {});

  const ytId = youtubeId(route.video_url);
  const mapEmbed = buildDirectionsEmbed(route.waypoints_geo || []);

  return (
    <div data-testid={`route-detail-${route.slug}`}>
      {/* HERO */}
      <section className="relative h-[88vh] min-h-[640px] w-full overflow-hidden flex items-end">
        <img src={route.hero_image || route.cover_image} alt={route.name} className="absolute inset-0 w-full h-full object-cover slow-zoom" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0B0B]/30 via-[#0B0B0B]/40 to-[#0B0B0B]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0B0B]/85 via-transparent to-transparent" />
        <div className="container-rotta relative z-10 pb-20 md:pb-28">
          <div className="text-[11px] tracking-[0.3em] uppercase text-white/70 mb-4 flex items-center gap-2 fade-up">
            <Link to="/rotas" className="hover:text-[#C6A762]">Rotas</Link>
            <ChevronRight size={12} /><span className="text-[#C6A762]">{route.state}</span>
          </div>
          {route.verified && (
            <div data-testid="verified-badge" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#0B0B0B]/85 backdrop-blur border border-[#C6A762]/50 text-[#C6A762] text-[10px] tracking-[0.2em] uppercase font-medium mb-6 fade-up">
              <BadgeCheck size={12} /> Rota Verificada pela ROTTA
            </div>
          )}
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white leading-[0.95] fade-up delay-1">{route.name}</h1>
          <div className="flex flex-wrap items-center gap-5 text-sm text-white/80 mt-6 fade-up delay-2">
            <span className="flex items-center gap-2"><MapPin size={14} className="text-[#C6A762]" /> {route.state}</span>
            <span className="flex items-center gap-2"><RouteIcon size={14} className="text-[#C6A762]" /> {route.distance_km} km</span>
            <span className="flex items-center gap-2"><Mountain size={14} className="text-[#C6A762]" /> {route.difficulty}</span>
            <span className="flex items-center gap-2"><Clock size={14} className="text-[#C6A762]" /> {route.estimated_time}</span>
            {route.ratings_summary?.count > 0 && (
              <span className="flex items-center gap-1.5 text-[#C6A762]">
                <Star size={14} fill="#C6A762" /> <span className="font-medium">{route.ratings_summary.average}</span>
                <span className="text-white/60">({route.ratings_summary.count})</span>
              </span>
            )}
          </div>
        </div>
      </section>

      {/* VÍDEO — destaque logo no início (oculto se não houver) */}
      {ytId && (
        <section className="section" data-testid="route-video">
          <div className="container-rotta">
            <Kicker>Conheça esta rota</Kicker>
            <h2 className="font-display text-4xl md:text-5xl text-white leading-[1.05] mb-10">Veja a rota em movimento.</h2>
            <div className="card-rotta overflow-hidden relative aspect-video group">
              <iframe
                src={`https://www.youtube.com/embed/${ytId}`}
                title={`Vídeo ${route.name}`}
                className="w-full h-full"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <div className="mt-4 flex justify-end">
              <a href={`https://www.youtube.com/watch?v=${ytId}`} target="_blank" rel="noreferrer" data-testid="route-video-watch" className="btn-ghost px-5 py-2.5 rounded-full text-[11px] inline-flex items-center gap-2">
                <Play size={12} fill="currentColor" /> Assistir no YouTube
              </a>
            </div>
          </div>
        </section>
      )}

      {/* RESUMO + INFO */}
      <section className={`section ${ytId ? "pt-0" : ""}`} data-testid="route-about">
        <div className="container-rotta grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-7">
            <Kicker>Resumo</Kicker>
            <h2 className="font-display text-4xl md:text-5xl text-white leading-[1.05]">{route.short_description}</h2>
            <div className="mt-6 blog-content">
              {splitParagraphs(route.description).map((p, i) => (
                <p key={i} className="text-[#B8B8B8] leading-relaxed text-base mb-5 last:mb-0">{p}</p>
              ))}
            </div>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={0.1}>
            <div className="card-rotta p-7 bg-[#161616]">
              <div className="font-display tracking-wider text-white uppercase text-sm mb-5">Informações Técnicas</div>
              <ul className="divide-y divide-[#2a2a2a]">
                <Info icon={<RouteIcon size={14} />} label="Distância" value={`${route.distance_km} km`} />
                <Info icon={<Clock size={14} />} label="Tempo estimado" value={route.estimated_time} />
                <Info icon={<Bike size={14} />} label="Tipo de moto" value={route.bike_type || "Qualquer motocicleta"} />
                <Info icon={<Mountain size={14} />} label="Terreno" value={route.terrain} />
                <Info icon={<CalendarDays size={14} />} label="Melhor época" value={route.best_season || "Ano todo"} />
                <Info icon={<MapPin size={14} />} label="Região" value={route.region || route.state} />
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* HISTÓRIA */}
      {route.history && (
        <section className="section pt-0" data-testid="route-history">
          <div className="container-rotta grid lg:grid-cols-12 gap-12 items-start">
            <Reveal className="lg:col-span-5">
              <Kicker>História da Rota</Kicker>
              <h2 className="font-display text-4xl md:text-5xl text-white leading-[1.05]">A herança de quem <span className="text-gold-gradient">abriu o caminho.</span></h2>
            </Reveal>
            <Reveal className="lg:col-span-7" delay={0.1}>
              <div className="blog-content">
                {splitParagraphs(route.history).map((p, i) => (
                  <p key={i} className="text-[#D6D6D6] leading-relaxed text-base mb-5 last:mb-0">{p}</p>
                ))}
              </div>
              {route.technical_info && (
                <div className="mt-5 blog-content">
                  {splitParagraphs(route.technical_info).map((p, i) => (
                    <p key={i} className="text-[#B8B8B8] leading-relaxed text-sm mb-4 last:mb-0">{p}</p>
                  ))}
                </div>
              )}
            </Reveal>
          </div>
        </section>
      )}

      {/* MAPA — trajeto real do Google Maps */}
      {(mapEmbed || route.google_maps_url) && (
        <section className="section pt-0" data-testid="route-map">
          <div className="container-rotta">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
              <div>
                <Kicker>Mapa Interativo</Kicker>
                <h2 className="font-display text-4xl md:text-5xl text-white leading-[1.05]">O trajeto real.</h2>
              </div>
              {route.google_maps_url && (
                <a href={route.google_maps_url} target="_blank" rel="noreferrer" data-testid="route-open-maps" className="btn-gold px-6 py-3 rounded-full text-xs inline-flex items-center gap-2">
                  Abrir no Google Maps <ExternalLink size={14} />
                </a>
              )}
            </div>
            {mapEmbed ? (
              <div className="card-rotta overflow-hidden">
                <iframe src={mapEmbed} title={`Mapa ${route.name}`} className="w-full h-[520px]" loading="lazy" allowFullScreen />
              </div>
            ) : (
              <div className="card-rotta p-10 bg-[#141414] text-center">
                <p className="text-[#B8B8B8]">Abra o trajeto completo diretamente no Google Maps.</p>
                <a href={route.google_maps_url} target="_blank" rel="noreferrer" className="btn-gold px-6 py-3 rounded-full text-xs inline-flex items-center gap-2 mt-5">
                  Abrir no Google Maps <ExternalLink size={14} />
                </a>
              </div>
            )}
          </div>
        </section>
      )}

      {/* PERCURSO */}
      {route.waypoints?.length > 0 && (
        <section className="section pt-0" data-testid="route-waypoints">
          <div className="container-rotta">
            <Kicker>Percurso</Kicker>
            <h2 className="font-display text-4xl md:text-5xl text-white leading-[1.05] mb-12">Pontos de passagem.</h2>
            <ol className="relative space-y-5 md:space-y-0 md:grid md:grid-cols-3 lg:grid-cols-6 md:gap-3">
              {route.waypoints.map((wp, i) => (
                <li key={wp} className="relative md:text-center">
                  <div className="flex md:flex-col items-center gap-4 md:gap-3">
                    <div className="w-10 h-10 rounded-full grid place-items-center border border-[#C6A762]/40 text-[#C6A762] bg-[#C6A762]/8 font-display">{String(i + 1).padStart(2, "0")}</div>
                    <div className="text-white text-sm font-medium md:text-center">{wp}</div>
                  </div>
                  {i < route.waypoints.length - 1 && <div className="hidden md:block absolute top-5 left-[calc(50%+24px)] right-[-50%] h-px bg-[#2a2a2a]" />}
                </li>
              ))}
            </ol>
          </div>
        </section>
      )}

      {/* PONTOS DE INTERESSE */}
      {route.points_of_interest?.length > 0 && (
        <section className="section pt-0" data-testid="route-poi">
          <div className="container-rotta">
            <Kicker>Pontos de Interesse</Kicker>
            <h2 className="font-display text-4xl md:text-5xl text-white leading-[1.05] mb-12">Paradas que valem o desvio.</h2>
            {Object.entries({ cafe: "Cafés", restaurant: "Restaurantes", hotel: "Hospedagens", viewpoint: "Mirantes", fuel: "Postos de Combustível", repair: "Oficinas", atrativo: "Atrativos" }).map(([key, label]) => {
              const items = groupPOI[key];
              if (!items?.length) return null;
              return (
                <div key={key} className="mb-12 last:mb-0" data-testid={`poi-group-${key}`}>
                  <div className="flex items-center gap-3 mb-5">
                    <span className="text-[#C6A762]">{(() => { const Ic = ICONS[key] || Star; return <Ic size={16} />; })()}</span>
                    <h3 className="font-display tracking-wider text-white uppercase text-sm">{label}</h3>
                    <span className="text-[11px] text-[#7d7d7d]">{items.length}</span>
                  </div>
                  <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {items.map((poi, i) => <POICard key={`${key}-${i}`} poi={poi} />)}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* DESTAQUES */}
      {route.highlights?.length > 0 && (
        <section className="section pt-0" data-testid="route-highlights">
          <div className="container-rotta grid lg:grid-cols-12 gap-12 items-center">
            <Reveal className="lg:col-span-5">
              <Kicker>Destaques</Kicker>
              <h2 className="font-display text-4xl md:text-5xl text-white leading-[1.05]">O que você vai ver no caminho.</h2>
            </Reveal>
            <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {route.highlights.map((h) => (
                <div key={h} className="border border-[#2a2a2a] hover:border-[#C6A762]/40 transition-colors rounded-xl p-4 bg-[#141414]">
                  <div className="text-white text-sm font-medium leading-tight">{h}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CUIDADOS + MELHOR ÉPOCA */}
      {(route.tips?.length > 0 || route.best_season) && (
        <section className="section pt-0" data-testid="route-tips">
          <div className="container-rotta grid lg:grid-cols-12 gap-8">
            {route.tips?.length > 0 && (
              <div className="lg:col-span-8 card-rotta p-8 md:p-10 bg-gradient-to-br from-[#181818] to-[#101010]">
                <div className="flex items-center gap-3 text-[#C6A762] mb-4"><AlertTriangle size={16} /><span className="text-[11px] tracking-[0.35em] uppercase">Cuidados Importantes</span></div>
                <h2 className="font-display text-3xl md:text-4xl text-white leading-[1.05] mb-7">Antes de pegar a estrada.</h2>
                <ul className="grid md:grid-cols-2 gap-3">
                  {route.tips.map((t, i) => (
                    <li key={i} className="flex items-start gap-3 text-[#E8E8E8]">
                      <div className="w-6 h-6 flex-shrink-0 rounded-full grid place-items-center bg-[#C6A762]/15 text-[#C6A762] text-[10px] font-display mt-0.5">{String(i + 1).padStart(2, "0")}</div>
                      <span className="text-sm leading-relaxed">{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {route.best_season && (
              <aside className="lg:col-span-4 card-rotta p-8 bg-[#161616] flex flex-col">
                <div className="flex items-center gap-3 text-[#C6A762] mb-4"><CalendarDays size={16} /><span className="text-[11px] tracking-[0.35em] uppercase">Melhor Época</span></div>
                <div className="font-display text-3xl text-white leading-tight">{route.best_season}</div>
                <p className="text-[#B8B8B8] text-sm mt-4 leading-relaxed">Período com menor índice de chuvas, temperaturas amenas e visibilidade ideal para apreciar mirantes e estradas sinuosas.</p>
              </aside>
            )}
          </div>
        </section>
      )}

      {/* GALERIA */}
      {route.gallery?.length > 0 && (
        <section className="section pt-0" data-testid="route-gallery">
          <div className="container-rotta">
            <div className="flex items-center gap-3 mb-3"><ImageIcon size={16} className="text-[#C6A762]" /><span className="text-[11px] tracking-[0.35em] uppercase text-[#C6A762]">Galeria</span></div>
            <h2 className="font-display text-4xl md:text-5xl text-white leading-[1.05] mb-10">Imagens da rota.</h2>
            <div className="grid gap-4 md:grid-cols-3">
              {route.gallery.map((src, i) => (
                <button key={i} onClick={() => setLightbox(src)} data-testid={`gallery-${i}`} className="card-rotta img-zoom aspect-[4/3] block overflow-hidden">
                  <img src={src} alt={`Galeria ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* AVALIAÇÕES REAIS */}
      <RouteReviews
        slug={route.slug}
        reviews={route.reviews || []}
        summary={route.ratings_summary}
        onAdded={(res) => setRoute((r) => ({ ...r, reviews: [...(r.reviews || []), res.review], ratings_summary: res.ratings_summary }))}
      />

      {/* CONTINUE A ESTRADA — 3 rotas recomendadas (curadoria) */}
      {recommended.length > 0 && (
        <section className="section pt-0" data-testid="route-related">
          <div className="container-rotta">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
              <div>
                <Kicker>Continue a estrada</Kicker>
                <h2 className="font-display text-4xl md:text-5xl text-white leading-[1.05]">Rotas para descobrir depois.</h2>
              </div>
              <Link to="/rotas" data-testid="route-see-all" className="text-[#C6A762] text-xs tracking-[0.25em] uppercase inline-flex items-center gap-2 hover:translate-x-1 transition-transform">Ver todas as rotas <ArrowRight size={14} /></Link>
            </div>
            <div className="grid gap-7 md:grid-cols-3">
              {recommended.slice(0, 3).map((r) => <RouteCard key={r.slug} route={r} />)}
            </div>
          </div>
        </section>
      )}

      {lightbox && (
        <div onClick={() => setLightbox(null)} className="fixed inset-0 z-[100] bg-[#0B0B0B]/95 backdrop-blur grid place-items-center p-6 cursor-zoom-out" data-testid="lightbox">
          <img src={lightbox} alt="" className="max-h-[90vh] max-w-[90vw] rounded-xl shadow-2xl" />
        </div>
      )}
    </div>
  );
}

function Kicker({ children }) {
  return (
    <div className="flex items-center gap-3 text-[#C6A762] mb-4">
      <span className="gold-divider" />
      <span className="text-[11px] tracking-[0.35em] uppercase">{children}</span>
    </div>
  );
}

function Info({ icon, label, value }) {
  return (
    <li className="flex items-center justify-between py-3.5">
      <span className="flex items-center gap-3 text-[#B8B8B8] text-sm"><span className="text-[#C6A762]">{icon}</span>{label}</span>
      <span className="text-white text-sm font-medium text-right max-w-[60%]">{value}</span>
    </li>
  );
}

function POICard({ poi }) {
  const Ic = ICONS[poi.type] || Star;
  return (
    <article className="card-rotta p-6 flex flex-col gap-3" data-testid={`poi-${poi.type}`}>
      {poi.image && (
        <div className="img-zoom -m-6 mb-0 aspect-video overflow-hidden rounded-t-[18px]">
          <img src={poi.image} alt={poi.name} className="w-full h-full object-cover" loading="lazy" />
        </div>
      )}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 grid place-items-center rounded-full bg-[#C6A762]/10 text-[#C6A762] border border-[#C6A762]/30"><Ic size={16} /></div>
        <div>
          <div className="font-display text-lg text-white leading-tight">{poi.name}</div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-[#7d7d7d]">{poi.location}</div>
        </div>
      </div>
      {poi.rating && (
        <div className="flex items-center gap-1.5 text-xs text-[#C6A762]"><Star size={12} fill="#C6A762" /> <span className="font-medium">{poi.rating}</span><span className="text-[#7d7d7d]">no Google</span></div>
      )}
      <p className="text-sm text-[#B8B8B8] leading-relaxed flex-1">{poi.description}</p>
      {poi.google_url && (
        <a href={poi.google_url} target="_blank" rel="noreferrer" className="btn-ghost inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-[11px] mt-2">Ver localização <ExternalLink size={12} /></a>
      )}
    </article>
  );
}
